import type { AdditionalFilterValues, ICandidate } from "@bbj-types/candidate";
import type { ICandidateFieldSchema, ICandidateFieldSchemaWithOptions } from "@bbj-types/organisation";
import { CandidateFieldFeatures } from "@bbj-types/organisation/enum";
import type { ISettingValueMaster } from "@bbj-types/setting";
import { useGlobalContext } from "@root/context";
import { GENDER_FILTER_OPTIONS } from "@root/utils/constants/adminInformation";
import equal from "fast-deep-equal/react";
import { type Dispatch, useMemo, useState } from "react";
import type { IAddParticipantExtendedFilters, IAddParticipantFilters, TAddParticipantFiltersAction } from "../types";
export interface IAdditionalStaticFilters {
  gender: ICandidate["gender"][];
  objectives: string[];
  birthDate: { startDate: Date | null; endDate: Date | null };
}

type TExtendedAdditionalFiltersOptions = {
  label: string;
  value: keyof IAddParticipantExtendedFilters;
};

export type TExtendedAddtionalFiltersValues = IAddParticipantFilters[keyof IAddParticipantExtendedFilters];
type TCandidateSchemaFilters = Pick<
  ICandidateFieldSchemaWithOptions,
  "label" | "value" | "type" | "isActive" | "options" | "features"
>;

export type TAdditionalFilterOption = TCandidateSchemaFilters | TExtendedAdditionalFiltersOptions;

export const isCandidateSchemaFilterOption = (
  value: TCandidateSchemaFilters | TExtendedAdditionalFiltersOptions
): value is TCandidateSchemaFilters => {
  return (value as TCandidateSchemaFilters).isActive !== undefined;
};

const extendedAdditionalFilters: TExtendedAdditionalFiltersOptions[] = [
  {
    label: "Lieu de résidence",
    value: "place",
  },
];

const buildFakeSettingValue = (label: string, value: string, group?: string): ISettingValueMaster => {
  return {
    label,
    value,
    group,
    active: true,
    createByDefault: false,
    isAutoTask: false,
    isEditable: false,
    searchByDefault: false,
  };
};

const buildAdditionalStaticFilterOption = (
  label: string,
  value: keyof IAdditionalStaticFilters,
  type: ICandidateFieldSchema["type"],
  options?: ISettingValueMaster[]
): TAdditionalFilterOption => {
  const fakeOption: TAdditionalFilterOption = {
    features: CandidateFieldFeatures.FILTERABLE,
    isActive: true,
    label,
    value,
    type,
  };
  if (options) {
    fakeOption.options = {
      domain: "",
      settingType: "",
      label: "",
      values: options,
      public: false,
    };
  }
  return fakeOption;
};

export const genderAdditionalFilterOption = buildAdditionalStaticFilterOption(
  "Civilité",
  "gender",
  "list",
  GENDER_FILTER_OPTIONS.map((option) => buildFakeSettingValue(option.label, option.value))
);

const birthDateAdditionalFilterOption = buildAdditionalStaticFilterOption("Date de naissance", "birthDate", "date");

export const resolverAdditionalFiltersUpdate = <T extends Pick<IAddParticipantFilters, "additionalFilters">>(
  state: T,
  field: string,
  value: AdditionalFilterValues | null
): T => {
  if (equal(state.additionalFilters[field], value)) {
    return state;
  }
  return { ...state, additionalFilters: { ...state.additionalFilters, [field]: value } };
};

export const resolverAdditionalFiltersDelete = <T extends Pick<IAddParticipantFilters, "additionalFilters">>(
  state: T,
  field: string
): T => {
  if (field in state.additionalFilters) {
    const newAdditionalFilters = { ...state.additionalFilters };
    delete newAdditionalFilters[field];
    return { ...state, additionalFilters: newAdditionalFilters };
  }
  return state;
};

export const useAdditionalFilters = (dispatch: Dispatch<TAddParticipantFiltersAction>) => {
  const { candidateSchemas, challenges } = useGlobalContext();
  const [selectedOptions, setSelectedOptions] = useState<TAdditionalFilterOption[]>([]);

  const options = useMemo(() => {
    const options = candidateSchemas
      .reduce<TAdditionalFilterOption[]>((options, schema) => {
        for (const fieldSchema of schema.values) {
          if (
            fieldSchema.isActive &&
            fieldSchema.features & CandidateFieldFeatures.SPOTLIGHT &&
            !selectedOptions.some((selectedOption) => selectedOption.value === fieldSchema.value)
          ) {
            selectedOptions.push(fieldSchema);
          } else if (
            fieldSchema.isActive &&
            fieldSchema.features & CandidateFieldFeatures.FILTERABLE &&
            !selectedOptions.some((selectedOption) => selectedOption.value === fieldSchema.value)
          ) {
            options.push(fieldSchema);
          }
        }
        return options;
      }, [])
      .concat(
        extendedAdditionalFilters.filter(
          (option) => !selectedOptions.some((selectedOption) => selectedOption.value === option.value)
        )
      );
    if (!selectedOptions.some((selectedOption) => selectedOption.value === "gender")) {
      options.push(genderAdditionalFilterOption);
    }
    if (!selectedOptions.some((selectedOption) => selectedOption.value === "birthDate")) {
      options.push(birthDateAdditionalFilterOption);
    }
    if (!selectedOptions.some((selectedOption) => selectedOption.value === "objectives")) {
      options.push(
        buildAdditionalStaticFilterOption(
          "Objectifs en cours",
          "objectives",
          "list",
          challenges.reduce<ISettingValueMaster[]>((acc, challenge) => {
            for (const objective of challenge.objectives) {
              if (objective.active) {
                acc.push(buildFakeSettingValue(objective.name, objective._id, challenge.name));
              }
            }
            return acc;
          }, [])
        )
      );
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [candidateSchemas, selectedOptions, challenges]);

  const handleSelectOption = (option: TAdditionalFilterOption) => {
    setSelectedOptions((selectedOptions) => [...selectedOptions, option]);
  };

  const handleDeleteOption = (option: TAdditionalFilterOption) => {
    setSelectedOptions((selectedOptions) =>
      selectedOptions.filter((selectedOption) => selectedOption.value !== option.value)
    );
    if (isCandidateSchemaFilterOption(option)) {
      dispatch({ type: "additionalFilters:delete", payload: option.value });
    } else {
      dispatch({ type: "extendedFilters:delete", payload: option.value });
    }
  };

  const handleChangeAdditionalField = (option: TAdditionalFilterOption, value: AdditionalFilterValues | null) => {
    if (isCandidateSchemaFilterOption(option)) {
      dispatch({
        type: "additionalFilters:update",
        payload: {
          field: option.value,
          value: value,
        },
      });
    }
  };

  const handleChangeExtendedField = (option: TAdditionalFilterOption, value: TExtendedAddtionalFiltersValues) => {
    if (!isCandidateSchemaFilterOption(option)) {
      dispatch({
        type: option.value,
        payload: value,
      });
    }
  };

  return {
    options,
    selectedOptions,
    handleSelectOption,
    handleDeleteOption,
    handleChangeAdditionalField,
    handleChangeExtendedField,
  };
};
